import { guidedOnboardingContent as defaultGuidedOnboardingContent, GuidedOnboardingService } from '@tableau/guided-onboarding';
import { Action } from '@tableau/guided-onboarding';
import { useQuery } from '@tanstack/react-query';

import { Messages } from '~src/codegen/Localize';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { useEventState, VPCEvent } from '~src/commonInfra/services/VPCEvent';
import { setGuidedOnboardingAction } from '~src/home/requests/SetGuidedOnboardingActionsRequest';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { sendUsageEvent } from '~src/spacesPlatform/hooks/useUsage';


export const WelcomeBannerLocalStorageKey = 'GuidedOnboardingWelcomeBanner';
export type CloseBannerSource = 'closeButton' | 'helpMenu';

export const WelcomeBannerFeatureName = 'WelcomeBanner';
export const WelcomeBannerCardFeatureName = 'WelcomeBannerCard';
export enum WelcomeBannerClickType { 'card', 'filmstripLeftArrow', 'filmstripRightArrow', 'toggle' }

interface GuidedOnboardingLocalStorageData {
    isBannerVisible: boolean;
    shouldShowPopover: boolean;
}

interface GuidedOnboardingContent {
    actions: Action[];
}

export type GuidedOnboardingBannerCardData = Action & { isCompleted: boolean };

export class GuidedOnboardingBannerService {

    public static instance = new GuidedOnboardingBannerService();
    public bannerVisibilityChanged = new VPCEvent<{ visible: boolean, source?: CloseBannerSource }>();
    public bannerLayoutChanged = new VPCEvent<void>();
    private isBannerVisible: boolean;
    private shouldShowPopover: boolean;
    private guidedOnboardingContent: GuidedOnboardingContent;

    constructor(guidedOnboardingContentParam?: GuidedOnboardingContent) {
        this.guidedOnboardingContent = guidedOnboardingContentParam || defaultGuidedOnboardingContent;
        const sessionManagementService = getSessionManagementServiceInstance();
        sessionManagementService.bootstrapSessionPromise.then(() => {
            this.loadLocalStorageValues();
        });
    }

    private loadLocalStorageValues() {
        const localStorageData = this.getWelcomeBannerDataFromLocalStorage();
        this.shouldShowPopover = localStorageData.shouldShowPopover;
        this.isBannerVisible = localStorageData.isBannerVisible;
    }

    private getWelcomeBannerDataFromLocalStorage(): GuidedOnboardingLocalStorageData {
        const storedValue = localStorage.getItem(this.getLocalStorageKey());
        if (storedValue) {
            try {
                const parsed = JSON.parse(storedValue);
                return {
                    // default to true if not explicitly false
                    isBannerVisible: parsed.isBannerVisible !== false,
                    shouldShowPopover: parsed.shouldShowPopover !== false
                };
            } catch {
                return this.getDefaultWelcomeBannerData();
            }
        }
        return this.getDefaultWelcomeBannerData();
    }

    private getDefaultWelcomeBannerData(): GuidedOnboardingLocalStorageData {
        return {
            isBannerVisible: true,
            shouldShowPopover: true
        };
    }

    private getLocalStorageKey() {
        const sessionManagementService = getSessionManagementServiceInstance();
        return WelcomeBannerLocalStorageKey + '-' + sessionManagementService.sessionUserLuid();
    }

    private saveWelcomeBannerDataToLocalStorage(data: GuidedOnboardingLocalStorageData) {
        localStorage.setItem(this.getLocalStorageKey(), JSON.stringify(data));
    }

    public updateBannerVisibility(bannerState: boolean, source?: CloseBannerSource) {
        this.isBannerVisible = bannerState;
        this.saveWelcomeBannerDataToLocalStorage({
            isBannerVisible: bannerState,
            shouldShowPopover: this.shouldShowPopover
        });
        GuidedOnboardingBannerService.instance.bannerVisibilityChanged.trigger({ visible: bannerState, source });
        sendUsageEvent(WelcomeBannerFeatureName, 'click_through', {
            a: WelcomeBannerClickType[WelcomeBannerClickType.toggle],
            b: source,
            c: bannerState ? 1 : 0
        });
    }

    public getBannerVisibility(): boolean {
        return this.isBannerVisible;
    }

    public updatePopoverVisibility(popoverVisibility: boolean) {
        this.shouldShowPopover = popoverVisibility;
        this.saveWelcomeBannerDataToLocalStorage({
            isBannerVisible: this.isBannerVisible,
            shouldShowPopover: popoverVisibility
        });
    }

    public getPopoverVisibility(): boolean {
        return this.shouldShowPopover;
    }

    public async getBannerCardData(): Promise<GuidedOnboardingBannerCardData[]> {
        return await GuidedOnboardingService.instance.getActions();
    }

    public async setOnboardingActionAsComplete(actionId: string): Promise<void> {
        try {
            await setGuidedOnboardingAction({
                action: {
                    actionId,
                    isCompleted: true
                }
            });

        } catch (e) {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.guidedOnboardingServiceUpdateActionsError() });
        }
    }

}

export const ServiceHooks = {
  useBannerVisibility,
  useCardData,
};

function useBannerVisibility() {
  const { args } = useEventState(GuidedOnboardingBannerService.instance.bannerVisibilityChanged, () => {
    return {
      visible: GuidedOnboardingBannerService.instance.getBannerVisibility()
    };
  });

  return { hidden: !args.visible };
}

const CARD_DATA_QUERY_KEY = ['GuidedOnboardingCardData'];

function useCardData() {
  return useQuery({
    queryKey: CARD_DATA_QUERY_KEY,
    queryFn: () => GuidedOnboardingBannerService.instance.getBannerCardData(),
    refetchOnWindowFocus: false
  });
}